'use client';

import { createContext, useContext, ReactNode, useMemo, useCallback, useEffect } from 'react';
import { useCustomerData, CustomerData, CustomerDocumentsStatus } from '../hooks/useCustomerData';
import { subscribeContractAccepted } from '@/utils/contractAcceptanceBroadcast';

type CustomerDataContextType = {
    customerData: CustomerData | null;
    isLoading: boolean;
    errorStatus: number | null;
    refreshCustomerData: () => void;
    // Helper computed properties for easier access
    isAccountFrozen: boolean;
    isAddVehicleEnabled: boolean;
    isClientArchived: boolean;
    isDockReceiptShown: boolean;
    isEndUserUltimateDestinationAccepted: boolean;
    isProtectionPlanAccepted: boolean;
    isProtectionPlanShown: boolean;
    areNraTermsAndConditionsAccepted: boolean;
    areShippingRatesAccepted: boolean;
    areTermsAndConditionsAccepted: boolean;
    customerDocumentsStatus: CustomerDocumentsStatus | null;
    legacySessionInitiationToken: string | null;
    destinationPort: string;
    destinationPortId: number;
};

const CustomerDataContext = createContext<CustomerDataContextType | undefined>(undefined);

type CustomerDataProviderProps = {
    children: ReactNode;
};

export const CustomerDataProvider = ({ children }: CustomerDataProviderProps) => {
    const { data, isLoading, errorStatus, fetchData } = useCustomerData();

    const refreshCustomerData = useCallback(() => {
        fetchData();
    }, [fetchData]);

    // A contract accepted in another tab changes the acceptance flags server-side;
    // refetch so banners, menu icons and enforcement guards in this tab update too.
    // Background mode: no isLoading toggle (consumers unmount into spinners on it,
    // which would silently close an open certificate dialog) and no error toasts.
    useEffect(() => {
        return subscribeContractAccepted(() => {
            fetchData({ background: true });
        });
    }, [fetchData]);

    // Helper computed properties for easier access throughout the app
    const isAccountFrozen = useMemo(() => data?.isAccountFrozen ?? false, [data?.isAccountFrozen]);
    const isAddVehicleEnabled = useMemo(() => data?.isAddVehicleEnabled ?? false, [data?.isAddVehicleEnabled]);
    const isClientArchived = useMemo(() => data?.isClientArchived ?? false, [data?.isClientArchived]);
    const isDockReceiptShown = useMemo(() => data?.isDockReceiptShown ?? false, [data?.isDockReceiptShown]);
    const isEndUserUltimateDestinationAccepted = useMemo(
        () => data?.isEndUserUltimateDestinationAccepted ?? false,
        [data?.isEndUserUltimateDestinationAccepted]
    );
    const isProtectionPlanAccepted = useMemo(
        () => data?.isProtectionPlanAccepted ?? false,
        [data?.isProtectionPlanAccepted]
    );
    const isProtectionPlanShown = useMemo(() => data?.isProtectionPlanShown ?? false, [data?.isProtectionPlanShown]);
    const areNraTermsAndConditionsAccepted = useMemo(
        () => data?.areNraTermsAndConditionsAccepted ?? false,
        [data?.areNraTermsAndConditionsAccepted]
    );
    const areShippingRatesAccepted = useMemo(
        () => data?.areShippingRatesAccepted ?? false,
        [data?.areShippingRatesAccepted]
    );
    const areTermsAndConditionsAccepted = useMemo(
        () => data?.areTermsAndConditionsAccepted ?? false,
        [data?.areTermsAndConditionsAccepted]
    );
    const customerDocumentsStatus = useMemo(
        () => data?.customerDocumentsStatus ?? null,
        [data?.customerDocumentsStatus]
    );
    const legacySessionInitiationToken = useMemo(
        () => data?.legacySessionInitiationToken ?? null,
        [data?.legacySessionInitiationToken]
    );
    const destinationPort = useMemo(() => data?.destinationPort ?? null, [data?.destinationPort]);
    const destinationPortId = useMemo(() => data?.destinationPortId ?? null, [data?.destinationPortId]);

    const value: CustomerDataContextType = useMemo(
        () => ({
            customerData: data,
            isLoading,
            errorStatus,
            refreshCustomerData,
            isAccountFrozen,
            isAddVehicleEnabled,
            isClientArchived,
            isDockReceiptShown,
            isEndUserUltimateDestinationAccepted,
            isProtectionPlanAccepted,
            isProtectionPlanShown,
            areNraTermsAndConditionsAccepted,
            areShippingRatesAccepted,
            areTermsAndConditionsAccepted,
            customerDocumentsStatus,
            legacySessionInitiationToken,
            destinationPort,
            destinationPortId,
        }),
        [
            data,
            isLoading,
            errorStatus,
            refreshCustomerData,
            isAccountFrozen,
            isAddVehicleEnabled,
            isClientArchived,
            isDockReceiptShown,
            isEndUserUltimateDestinationAccepted,
            isProtectionPlanAccepted,
            isProtectionPlanShown,
            areNraTermsAndConditionsAccepted,
            areShippingRatesAccepted,
            areTermsAndConditionsAccepted,
            customerDocumentsStatus,
            legacySessionInitiationToken,
            destinationPort,
            destinationPortId,
        ]
    );

    return <CustomerDataContext.Provider value={value}>{children}</CustomerDataContext.Provider>;
};

export const useCustomerDataContext = (): CustomerDataContextType => {
    const context = useContext(CustomerDataContext);
    if (context === undefined) {
        throw new Error('useCustomerDataContext must be used within a CustomerDataProvider');
    }
    return context;
};

// For components that render both inside and outside the provider (navigation shells shared by
// the marketing site and the customer zone). Returns undefined instead of throwing, so callers
// never have to wrap the hook call in try/catch — which would break the rules of hooks.
export const useOptionalCustomerDataContext = (): CustomerDataContextType | undefined =>
    useContext(CustomerDataContext);
